package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/**
 * The tinted square a section header stands its glyph in.
 *
 * Centring lives here rather than beside the caller's `modifier` so that nothing a caller passes
 * can land between the box and its centring and leave the glyph in a corner.
 *
 * **Public because Silk only registers public top-level styles.** Declared `private` — which is
 * what it wants to be, since nothing outside this file has any business with it — the Kobweb plugin
 * skips it and `toModifier()` throws "Failed to convert a CssStyle to a modifier" at *runtime*, on
 * every screen that draws a badge. Nothing in the kit is a private `CssStyle`, and this is why.
 */
val LIconBadgeStyle = CssStyle.base {
    Modifier
        .size(40.px)
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .backgroundColor(colorMode.toSitePalette().accentSubtle)
        .color(colorMode.toSitePalette().accent)
        .borderRadius(Radius.Control)
        .padding(Space.SM)
}

/**
 * A glyph in a tinted square, marking the subject of a section header.
 *
 * **Extracted because it had been written three times and had already drifted.** The provider grid,
 * the provider detail header and the usage-trend header each built the identical recipe inline —
 * `accentSubtle` behind `accent`, `Radius.Control`, `Space.SM` of padding — and then disagreed
 * about the only two numbers that matter: two drew a 24px glyph in a 40px square and the third a
 * 28px glyph in a 44px one. Nobody chose that; it is what four numbers repeated by hand do, and no
 * single instance looked wrong enough for anyone to notice the set had stopped matching.
 *
 * Sizes are not parameters. A second size would be the same drift with a nicer spelling, and these
 * three are one badge doing one job. The glyph rung is [IconSize.XXL], which exists for this.
 *
 * Decorative by construction: every icon this wraps is already `aria-hidden`, and the heading
 * beside it carries the meaning. Nothing here adds a label, because a badge that announced itself
 * would have the section title read twice.
 */
@Composable
fun LIconBadge(modifier: Modifier = Modifier, icon: @Composable () -> Unit) {
    Div(LIconBadgeStyle.toModifier().then(modifier).toAttrs()) {
        icon()
    }
}
